import type {
  Chat,
  ChatList,
  ChatSummary,
  CreateChatRequest,
  DeleteChatRequest,
  GetChatRequest,
  RateMessageRequest,
  RenameChatRequest,
  StarChatRequest,
} from '@/data-sources/contract';
import { api, overrideExisting } from '@/store/api/api';
import { fromSource, type Sourced } from '@/store/api/fromSource';
import { tag } from '@/store/api/tags';

export const chatApi = api.injectEndpoints({
  overrideExisting,
  endpoints: (build) => ({
    listChats: build.query<ChatList, Sourced<void>>({
      queryFn: fromSource((source, _params, ctx) => source.listChats(ctx)),
      providesTags: (_r, _e, arg) => [tag('Chat', arg.source)],
    }),
    getChat: build.query<Chat | null, Sourced<GetChatRequest>>({
      queryFn: fromSource((source, params, ctx) => source.getChat(params, ctx)),
      providesTags: (_r, _e, arg) => [tag('Chat', arg.source, arg.params.chatId)],
    }),
    createChat: build.mutation<ChatSummary, Sourced<CreateChatRequest>>({
      queryFn: fromSource((source, params, ctx) => source.createChat(params, ctx)),
      invalidatesTags: (_r, e, arg) => (e ? [] : [tag('Chat', arg.source)]),
    }),
    renameChat: build.mutation<ChatSummary, Sourced<RenameChatRequest>>({
      queryFn: fromSource((source, params, ctx) => source.renameChat(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Chat', arg.source), tag('Chat', arg.source, arg.params.chatId)],
    }),
    starChat: build.mutation<ChatSummary, Sourced<StarChatRequest>>({
      queryFn: fromSource((source, params, ctx) => source.starChat(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Chat', arg.source), tag('Chat', arg.source, arg.params.chatId)],
    }),
    deleteChat: build.mutation<null, Sourced<DeleteChatRequest>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.deleteChat(params, ctx);
        return null;
      }),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Chat', arg.source), tag('Chat', arg.source, arg.params.chatId)],
    }),
    rateMessage: build.mutation<null, Sourced<RateMessageRequest>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.rateMessage(params, ctx);
        return null;
      }),
      invalidatesTags: (_r, e, arg) => (e ? [] : [tag('Chat', arg.source, arg.params.chatId)]),
    }),
  }),
});

export const {
  useListChatsQuery,
  useGetChatQuery,
  useCreateChatMutation,
  useRenameChatMutation,
  useStarChatMutation,
  useDeleteChatMutation,
  useRateMessageMutation,
} = chatApi;
